import { BreakpointObserver } from "@angular/cdk/layout";
import { Component, inject, Input, OnDestroy, OnInit } from "@angular/core";
import { toSignal } from "@angular/core/rxjs-interop";
import { FormGroup, ReactiveFormsModule } from "@angular/forms";
import { UiKitModule } from "@app/ui-kit.module";
import { ConfirmModalComponent } from "@components/confirm-modal/confirm-modal.component";
import { FileModalComponent } from "@components/file-modal/file-modal.component";
import { ImageCardComponent } from "@components/image-card/image-card.component";
import { ImageUploadOverlayComponent } from "@components/image-upload-overlay/image-upload-overlay.component";
import { ProjectDetailMobileImageCardComponent } from "@components/project-cards/project-detail-mobile-image-card/project-detail-mobile-image-card.component";
import type { ScopedProject } from "@domain/project/project-guards";
import { ProjectImageMetadata } from "@domain/project/project-image-metadata";
import { ProjectImageData, ProjectImageService, SyncState } from "@domain/project/project-image.service";
import { ProjectService } from "@domain/project/project.service";
import { UserService } from "@domain/user/user.service";
import { BusinessScope } from "@lib/constants/business-scope";
import { ModalOptions, ModalService } from "@odx/angular/components/modal";
import { filter, map, switchMap, tap } from "rxjs";
import { Breakpoints } from "../../../../styles/breakpoints";

export interface ProjectImageDataWithUrl extends ProjectImageData {
  previewUrl?: string;
}
@Component({
  selector: "app-image-upload",
  templateUrl: "./image-upload.component.html",
  styleUrls: ["./image-upload.component.scss"],
  imports: [
    UiKitModule,
    ReactiveFormsModule,
    ImageUploadOverlayComponent,
    ImageCardComponent,
    ProjectDetailMobileImageCardComponent,
  ],
  standalone: true,
})
export class ImageUploadComponent implements OnInit, OnDestroy {
  private breakpointObserver = inject(BreakpointObserver);
  private readonly modalService = inject(ModalService);
  private readonly userService = inject(UserService);
  private readonly projectService = inject(ProjectService);
  private readonly projectImageService = inject(ProjectImageService);
  protected isMobile = toSignal(this.breakpointObserver.observe(Breakpoints.mobile).pipe(map((result) => result.matches)), {
    initialValue: false,
  });
  protected isReadOnlyUser = toSignal(this.userService.user$.pipe(map((user) => user.readOnlyAccess)), { initialValue: false });
  protected hasWritePermission = toSignal(
    this.projectService.hasUserWritePermission$.pipe(filter((val): val is boolean => val !== undefined)),
    { initialValue: false },
  );
  protected isMonitoringScope = toSignal(
    this.userService.businessScope$.pipe(map((scope) => scope === BusinessScope.MONITORING)),
    { initialValue: false },
  );
  protected imageTagList = toSignal(this.projectImageService.getProjectImageTags(BusinessScope.MONITORING), { initialValue: [] });

  @Input() set projectData(project: ScopedProject) {
    this.project = project;
    this.images = [];
    if (this.expandedImageId && !this.images.some((image) => image.imageMetadata.id === this.expandedImageId)) {
      this.expandedImageId = undefined;
    }
  }

  project!: ScopedProject;
  images: ProjectImageDataWithUrl[] = [];
  protected expandedImageId?: string;

  ngOnInit(): void {
    if (this.project.id) {
      this.loadImagePreviews(this.project.id).subscribe();
    }
  }

  ngOnDestroy(): void {
    this.images.forEach((img) => {
      if (img.previewUrl) {
        URL.revokeObjectURL(img.previewUrl);
      }
    });
  }
  onImageUploaded(projectImages: ProjectImageData[]) {
    this.projectImageService
      .saveImagesToIndexedDB(projectImages, this.project)
      .pipe(switchMap(() => this.loadImagePreviews(this.project.id)))
      .subscribe();
  }

  onDelete(image: ProjectImageMetadata) {
    const deleteImage$ = this.projectImageService.removeImage(image.id).pipe(
      tap(() => {
        this.projectImageService.deleteImageFromFloorplans(this.project, image);
      }),
      switchMap(() => this.loadImagePreviews(this.project.id)),
    );

    if (!this.project.cloudId) {
      deleteImage$.subscribe();
      return;
    }

    const isProjectPhotosMobile = this.isMobile();
    this.modalService
      .open(ConfirmModalComponent, {
        id: isProjectPhotosMobile ? "project-photos-confirm-modal" : undefined,
        data: { text: $localize`:@@modal.deleteImage:Wollen Sie das Bild wirklich löschen?` },
      })
      .onClose$.pipe(switchMap(() => deleteImage$))
      .subscribe();
  }

  onCardSubmit(form: FormGroup, image: ProjectImageMetadata) {
    if (this.project.cloudId && navigator.onLine) {
      image.name = form.value.name;
      image.description = form.value.description;
      image.tags = form.value.tags;

      this.projectImageService
        .updateImageInIndexedDB(image, this.project)
        .pipe(switchMap(() => this.loadImagePreviews(this.project.id)))
        .subscribe();
    }
  }

  openModal(image: ProjectImageMetadata) {
    const options: Partial<ModalOptions> = {
      data: {
        imageMetadata: image,
        project: this.project,
        headerText: this.isMobile() ? $localize`:@@imageUpload.preview.header:Vorschau` : undefined,
      },
    };

    this.modalService.open(FileModalComponent, options);
  }

  toggleImageExpansion(image: ProjectImageMetadata) {
    this.expandedImageId = this.expandedImageId === image.id ? undefined : image.id;
  }

  private loadImagePreviews(projectId: string) {
    return this.projectImageService.getImagesFromIndexedDB(projectId).pipe(
      tap((imagesFromDB) => {
        this.images.forEach((img) => {
          if (img.previewUrl) {
            URL.revokeObjectURL(img.previewUrl);
          }
        });

        this.images = imagesFromDB
          .filter((imageData) => imageData.syncState !== SyncState.DELETED)
          .map((imageData) => ({
            ...imageData,
            previewUrl: imageData.previewBlob ? URL.createObjectURL(imageData.previewBlob) : undefined,
          }));
      }),
    );
  }
}
